import { recipeTypeSchema, type RecipeType } from "@scoopulator/api-contracts";
import type { LucideIcon } from "lucide-react";
import {
  CakeSliceIcon,
  ChefHat,
  CherryIcon,
  CitrusIcon,
  DessertIcon,
  IceCream2Icon,
  IceCreamIcon,
  LayersIcon,
} from "lucide-react";

export enum RecipeTypeCategory {
  BASE = "base",
  MIX_IN = "mix-in",
  COMPOSITE = "composite",
  OTHER = "other",
}

export type CalculatorCompositionField =
  | "sweetness"
  | "totalSolids"
  | "fat"
  | "saturatedFatRatio"
  | "milkFat"
  | "sugars"
  | "msnf"
  | "stabilizersPerWater"
  | "stabilizers"
  | "emulsifiers"
  | "alcohol";

export type CalculatorAtAGlanceField = CalculatorCompositionField;

interface RecipeTypeMetadata {
  label: string;
  icon: LucideIcon;
  category: RecipeTypeCategory;
  calculator: {
    freezingAnalysis: boolean;
    compositionFields: readonly CalculatorCompositionField[];
    atAGlanceFields: readonly CalculatorAtAGlanceField[];
  };
}

export const RECIPE_TYPE_MAP = {
  ice_cream: {
    label: "Ice Cream Base",
    icon: IceCreamIcon,
    category: RecipeTypeCategory.BASE,
    calculator: {
      freezingAnalysis: true,
      compositionFields: [
        "totalSolids",
        "fat",
        "milkFat",
        "msnf",
        "sugars",
        "stabilizers",
        "emulsifiers",
        "alcohol",
      ],
      atAGlanceFields: [
        "sweetness",
        "totalSolids",
        "fat",
        "saturatedFatRatio",
        "milkFat",
        "sugars",
        "msnf",
        "stabilizersPerWater",
        "stabilizers",
        "emulsifiers",
        "alcohol",
      ],
    },
  },
  gelato: {
    label: "Gelato Base",
    icon: IceCream2Icon,
    category: RecipeTypeCategory.BASE,
    calculator: {
      freezingAnalysis: true,
      compositionFields: [
        "totalSolids",
        "fat",
        "milkFat",
        "msnf",
        "sugars",
        "stabilizers",
        "emulsifiers",
        "alcohol",
      ],
      atAGlanceFields: [
        "sweetness",
        "totalSolids",
        "fat",
        "saturatedFatRatio",
        "milkFat",
        "sugars",
        "msnf",
        "stabilizersPerWater",
        "stabilizers",
        "emulsifiers",
        "alcohol",
      ],
    },
  },
  sherbet: {
    label: "Sherbet Base",
    icon: CherryIcon,
    category: RecipeTypeCategory.BASE,
    calculator: {
      freezingAnalysis: true,
      compositionFields: [
        "totalSolids",
        "fat",
        "milkFat",
        "msnf",
        "sugars",
        "stabilizers",
        "emulsifiers",
        "alcohol",
      ],
      atAGlanceFields: [
        "sweetness",
        "totalSolids",
        "fat",
        "saturatedFatRatio",
        "milkFat",
        "sugars",
        "msnf",
        "stabilizersPerWater",
        "stabilizers",
        "emulsifiers",
        "alcohol",
      ],
    },
  },
  sorbet: {
    label: "Sorbet Base",
    icon: CitrusIcon,
    category: RecipeTypeCategory.BASE,
    calculator: {
      freezingAnalysis: true,
      compositionFields: [
        "totalSolids",
        "fat",
        "sugars",
        "stabilizers",
        "alcohol",
      ],
      atAGlanceFields: [
        "sweetness",
        "totalSolids",
        "fat",
        "saturatedFatRatio",
        "sugars",
        "stabilizersPerWater",
        "stabilizers",
        "alcohol",
      ],
    },
  },
  frozen_yogurt: {
    label: "Frozen Yogurt Base",
    icon: DessertIcon,
    category: RecipeTypeCategory.BASE,
    calculator: {
      freezingAnalysis: true,
      compositionFields: [
        "totalSolids",
        "fat",
        "milkFat",
        "msnf",
        "sugars",
        "stabilizers",
        "emulsifiers",
        "alcohol",
      ],
      atAGlanceFields: [
        "sweetness",
        "totalSolids",
        "fat",
        "saturatedFatRatio",
        "milkFat",
        "sugars",
        "msnf",
        "stabilizersPerWater",
        "stabilizers",
        "emulsifiers",
        "alcohol",
      ],
    },
  },
  ganache: {
    label: "Ganache Mix-in",
    icon: CakeSliceIcon,
    category: RecipeTypeCategory.MIX_IN,
    calculator: {
      freezingAnalysis: false,
      compositionFields: [
        "totalSolids",
        "fat",
        "milkFat",
        "sugars",
        "stabilizers",
      ],
      atAGlanceFields: ["sweetness", "totalSolids", "fat", "sugars"],
    },
  },
  composite: {
    label: "Composite",
    icon: LayersIcon,
    category: RecipeTypeCategory.COMPOSITE,
    calculator: {
      freezingAnalysis: true,
      compositionFields: [
        "totalSolids",
        "fat",
        "milkFat",
        "msnf",
        "sugars",
        "stabilizers",
        "emulsifiers",
        "alcohol",
      ],
      atAGlanceFields: [
        "sweetness",
        "totalSolids",
        "fat",
        "saturatedFatRatio",
        "milkFat",
        "sugars",
        "msnf",
        "stabilizersPerWater",
        "stabilizers",
        "emulsifiers",
        "alcohol",
      ],
    },
  },
  other: {
    label: "Other",
    icon: ChefHat,
    category: RecipeTypeCategory.OTHER,
    calculator: {
      freezingAnalysis: false,
      compositionFields: [
        "totalSolids",
        "fat",
        "milkFat",
        "msnf",
        "sugars",
        "stabilizers",
        "emulsifiers",
        "alcohol",
      ],
      atAGlanceFields: ["sweetness", "totalSolids", "fat", "sugars"],
    },
  },
} satisfies Record<RecipeType, RecipeTypeMetadata>;

export const getRecipeTypeLabel = (recipeType: RecipeType) => {
  return RECIPE_TYPE_MAP[recipeType]?.label ?? "Unknown";
};

export const getRecipeTypeCategoryLabel = (category: RecipeTypeCategory) => {
  switch (category) {
    case RecipeTypeCategory.BASE:
      return "Base";
    case RecipeTypeCategory.MIX_IN:
      return "Mix-ins";
    case RecipeTypeCategory.COMPOSITE:
      return "Composite";
    case RecipeTypeCategory.OTHER:
      return "Other";
  }
};

export const RECIPE_TYPE_LIST = recipeTypeSchema.options;
